import {useState, useCallback, useEffect, useRef} from 'react'
import {Network, Plus, Trash2, Loader2} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@/components/ui/card'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {TagInput} from '@/components/ui/tag-input'
import {MutedText} from '@/components/shared/MutedText'
import {DetailField} from '@/components/shared/DetailField'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import type {OutboundConnector, LdapOutboundConfig, LdapOutboundConfigUpdateRequest, OuRoutingRuleRequest} from '@/types/outbound-connector'

// RFC 4512 Object Class pattern: starts with letter, followed by letters/digits/hyphens
const RFC4512_OC_PATTERN = /^[a-zA-Z][a-zA-Z0-9-]*$/

const OPERATORS = ['EQUALS', 'NOT_EQUALS', 'CONTAINS', 'STARTS_WITH', 'IN'] as const
type Operator = typeof OPERATORS[number]

// ----------- Local state types -----------

interface OuRuleLocal {
    attribute: string
    operator: Operator
    // For non-IN operators: plain string. For IN operator: pipe-delimited string stored internally.
    value: string
    // For IN operator, we track the tags array in local state separately
    valueTags: string[]
    valueTagPending: string
    targetOu: string
}

interface LdapProvisioningFormState {
    objectClasses: string[]
    objectClassPending: string
    objectClassError: string | null
    dnTemplate: string
    dnTemplateError: string | null
    rules: OuRuleLocal[]
    defaultOu: string
    defaultOuError: string | null
}

// ----------- Helper: initialize form from saved config (issue 571.08 flat fields) -----------

function initFormFromConfig(config: LdapOutboundConfig | null): LdapProvisioningFormState {
    const objectClasses = config?.objectClasses ?? []
    const ouRoutingRules = config?.ouRoutingRules ?? []
    return {
        objectClasses,
        objectClassPending: '',
        objectClassError: null,
        dnTemplate: config?.dnTemplate ?? '',
        dnTemplateError: null,
        rules: ouRoutingRules.map(r => ({
            attribute: r.attribute ?? '',
            operator: (r.operator ?? 'EQUALS') as Operator,
            value: r.operator === 'IN' ? '' : (r.value ?? ''),
            valueTags: r.operator === 'IN' ? (r.value ? r.value.split('|') : []) : [],
            valueTagPending: '',
            targetOu: r.targetOu ?? '',
        })),
        defaultOu: config?.defaultOu ?? '',
        defaultOuError: null,
    }
}

// ----------- Serialization (flat fields per spec §3.12) -----------

interface SerializedConfig {
    dnTemplate: string | null
    defaultOu: string | null
    objectClasses: string[]
    ouRoutingRules: OuRoutingRuleRequest[]
}

function serializeConfig(form: LdapProvisioningFormState): SerializedConfig {
    // WHY: send the actual array — empty when the user removed every tag —
    // so the backend can distinguish "preserve" (omit / null, only used by
    // API-only callers) from "clear" (empty array). The editor always
    // owns the full list while it's mounted, so [] is never accidental.
    const objectClasses = form.objectClasses
    const dnTemplate = form.dnTemplate.trim() || null
    const defaultOu = form.defaultOu.trim() || null
    const ouRoutingRules: OuRoutingRuleRequest[] = form.rules.map(r => ({
        attribute: r.attribute || null,
        operator: r.operator,
        value: r.operator === 'IN'
            ? (r.valueTags.length > 0 ? r.valueTags.join('|') : null)
            : (r.value || null),
        targetOu: r.targetOu || null,
    }))
    return {dnTemplate, defaultOu, objectClasses, ouRoutingRules}
}

// ----------- Deep equality check for dirty tracking -----------

function statesEqual(a: LdapProvisioningFormState, b: LdapProvisioningFormState): boolean {
    return JSON.stringify(serializeConfig(a)) === JSON.stringify(serializeConfig(b))
}

// ----------- OuRoutingRulesEditor -----------

interface OuRoutingRulesEditorProps {
    rules: OuRuleLocal[]
    defaultOu: string
    defaultOuError: string | null
    onRulesChange: (rules: OuRuleLocal[]) => void
    onDefaultOuChange: (value: string) => void
    onValidateDefaultOu: () => void
    canUpdate: boolean
    i18nLdap: ReturnType<typeof useI18n>['connectors']['ldapProvisioningConfig']
}

function OuRoutingRulesEditor({
    rules,
    defaultOu,
    defaultOuError,
    onRulesChange,
    onDefaultOuChange,
    onValidateDefaultOu,
    canUpdate,
    i18nLdap,
}: OuRoutingRulesEditorProps) {
    function addRule() {
        onRulesChange([...rules, {
            attribute: '',
            operator: 'EQUALS',
            value: '',
            valueTags: [],
            valueTagPending: '',
            targetOu: '',
        }])
    }

    function removeRule(index: number) {
        onRulesChange(rules.filter((_, i) => i !== index))
    }

    function updateRule(index: number, updates: Partial<OuRuleLocal>) {
        onRulesChange(rules.map((r, i) => i === index ? {...r, ...updates} : r))
    }

    function handleOperatorChange(index: number, newOperator: Operator) {
        const rule = rules[index]
        if (newOperator === 'IN' && rule.operator !== 'IN') {
            // Switch to IN: clear plain value, start with empty tags
            updateRule(index, {operator: newOperator, value: '', valueTags: [], valueTagPending: ''})
        } else if (newOperator !== 'IN' && rule.operator === 'IN') {
            // Switch away from IN: clear tags, reset to plain input
            updateRule(index, {operator: newOperator, value: '', valueTags: [], valueTagPending: ''})
        } else {
            updateRule(index, {operator: newOperator})
        }
    }

    return (
        <div className="space-y-4">
            <div className="space-y-1">
                <Label>{i18nLdap.defaultOu}</Label>
                <Input
                    value={defaultOu}
                    onChange={e => onDefaultOuChange(e.target.value)}
                    onBlur={onValidateDefaultOu}
                    disabled={!canUpdate}
                    maxLength={500}
                    data-testid="ldap-default-ou"
                />
                {defaultOuError && (
                    <MutedText size="xs" className="text-destructive" data-testid="default-ou-error">{defaultOuError}</MutedText>
                )}
            </div>

            {rules.length > 0 && (
                <div className="overflow-x-auto" data-testid="ou-rules-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b text-muted-foreground">
                                <th className="text-left py-2 pr-2 font-medium" data-testid="col-ou-attribute">{i18nLdap.attribute}</th>
                                <th className="text-left py-2 pr-2 font-medium" data-testid="col-ou-operator">{i18nLdap.operator}</th>
                                <th className="text-left py-2 pr-2 font-medium" data-testid="col-ou-value">{i18nLdap.value}</th>
                                <th className="text-left py-2 pr-2 font-medium" data-testid="col-ou-target">{i18nLdap.targetOu}</th>
                                {canUpdate && <th className="w-8" data-testid="col-ou-actions"/>}
                            </tr>
                        </thead>
                        <tbody>
                            {rules.map((rule, index) => (
                                <tr key={index} className="border-b last:border-0" data-testid={`ou-rule-row-${index}`}>
                                    <td className="py-2 pr-2 min-w-[120px]">
                                        {canUpdate ? (
                                            <Input
                                                value={rule.attribute}
                                                onChange={e => updateRule(index, {attribute: e.target.value})}
                                                data-testid={`ou-rule-attribute-${index}`}
                                            />
                                        ) : (
                                            <span>{rule.attribute}</span>
                                        )}
                                    </td>
                                    <td className="py-2 pr-2 min-w-[140px]">
                                        {canUpdate ? (
                                            <Select
                                                value={rule.operator}
                                                onValueChange={(v) => handleOperatorChange(index, v as Operator)}
                                            >
                                                <SelectTrigger data-testid={`ou-rule-operator-${index}`}>
                                                    <SelectValue/>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {OPERATORS.map(op => (
                                                        <SelectItem key={op} value={op}>{op}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <span>{rule.operator}</span>
                                        )}
                                    </td>
                                    <td className="py-2 pr-2 min-w-[160px]">
                                        {rule.operator === 'IN' ? (
                                            canUpdate ? (
                                                <TagInput
                                                    values={rule.valueTags}
                                                    onAdd={tag => updateRule(index, {valueTags: [...rule.valueTags, tag], valueTagPending: ''})}
                                                    onRemove={i => updateRule(index, {valueTags: rule.valueTags.filter((_, ti) => ti !== i)})}
                                                    pending={rule.valueTagPending}
                                                    onPendingChange={v => updateRule(index, {valueTagPending: v})}
                                                    maxItems={50}
                                                    testId={`ou-rule-value-tags-${index}`}
                                                />
                                            ) : (
                                                <span>{rule.valueTags.join(', ')}</span>
                                            )
                                        ) : (
                                            canUpdate ? (
                                                <Input
                                                    value={rule.value}
                                                    onChange={e => updateRule(index, {value: e.target.value})}
                                                    data-testid={`ou-rule-value-${index}`}
                                                />
                                            ) : (
                                                <span>{rule.value}</span>
                                            )
                                        )}
                                    </td>
                                    <td className="py-2 pr-2 min-w-[160px]">
                                        {canUpdate ? (
                                            <Input
                                                value={rule.targetOu}
                                                onChange={e => updateRule(index, {targetOu: e.target.value})}
                                                data-testid={`ou-rule-target-ou-${index}`}
                                            />
                                        ) : (
                                            <span>{rule.targetOu}</span>
                                        )}
                                    </td>
                                    {canUpdate && (
                                        <td className="py-2">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => removeRule(index)}
                                                data-testid={`ou-rule-delete-${index}`}
                                            >
                                                <Trash2 className="h-4 w-4 text-muted-foreground"/>
                                            </Button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {canUpdate && (
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addRule}
                    data-testid="btn-add-ou-rule"
                >
                    <Plus className="h-4 w-4 mr-2"/>
                    {i18nLdap.addRule}
                </Button>
            )}

            {rules.length > 0 && (
                <MutedText size="xs">{i18nLdap.ouRoutingDescription}</MutedText>
            )}
        </div>
    )
}

// ----------- LdapProvisioningConfigEditor -----------

interface LdapProvisioningConfigEditorProps {
    connector: OutboundConnector
    canUpdate: boolean
    onDirtyChange?: (dirty: boolean) => void
}

export function LdapProvisioningConfigEditor({connector, canUpdate, onDirtyChange}: LdapProvisioningConfigEditorProps) {
    const services = useServices()
    const i18n = useI18n()
    const i18nLdap = i18n.connectors.ldapProvisioningConfig

    const savedConfig = connector.ldapConfig ?? null

    const [form, setForm] = useState<LdapProvisioningFormState>(() => initFormFromConfig(savedConfig))
    const [savedForm, setSavedForm] = useState<LdapProvisioningFormState>(() => initFormFromConfig(savedConfig))

    // WHY: hydrate from the connector prop only — every time the parent's
    // GET resolves a new ldapConfig reference, drop local state and re-seed
    // from the response so the form never renders stale values captured at
    // mount-time while a refetch was still in flight (issue 736 A.1).
    useEffect(() => {
        const newSaved = initFormFromConfig(connector.ldapConfig ?? null)
        setSavedForm(newSaved)
        setForm(newSaved)

    }, [connector.ldapConfig])

    const isDirty = !statesEqual(form, savedForm)

    // Notify parent of dirty state changes — use ref to avoid stale closure in effect
    const onDirtyChangeRef = useRef(onDirtyChange)
    onDirtyChangeRef.current = onDirtyChange
    useEffect(() => {
        onDirtyChangeRef.current?.(isDirty)
    }, [isDirty])

    const updateForm = useCallback((updates: Partial<LdapProvisioningFormState>) => {
        setForm(prev => ({...prev, ...updates}))
    }, [])

    // ---- Object Classes ----
    function handleAddObjectClass(tag: string) {
        if (!RFC4512_OC_PATTERN.test(tag)) {
            updateForm({objectClassError: i18n.connectors.ldapProvisioningConfig.objectClassesHint})
            return
        }
        if (form.objectClasses.length >= 20) return
        updateForm({
            objectClasses: [...form.objectClasses, tag],
            objectClassPending: '',
            objectClassError: null,
        })
        // Note: TagInput does not auto-clear pending; we handle it via objectClassPending: ''
    }

    function handleRemoveObjectClass(index: number) {
        updateForm({objectClasses: form.objectClasses.filter((_, i) => i !== index)})
    }

    // ---- DN Template ----
    function handleDnTemplateBlur() {
        const val = form.dnTemplate.trim()
        if (val && !val.includes('{ou}')) {
            updateForm({dnTemplateError: i18nLdap.dnTemplateHint})
        } else {
            updateForm({dnTemplateError: null})
        }
    }

    // ---- Default OU validation ----
    function handleValidateDefaultOu() {
        validateDefaultOu(form.defaultOu, form.rules)
    }

    function validateDefaultOu(defaultOu: string, rules: OuRuleLocal[]): boolean {
        if (rules.length > 0 && !defaultOu.trim()) {
            updateForm({defaultOuError: i18nLdap.defaultOuRequired})
            return false
        }
        if (defaultOu.trim().length > 500) {
            updateForm({defaultOuError: i18nLdap.defaultOuMaxLength})
            return false
        }
        updateForm({defaultOuError: null})
        return true
    }

    // ---- Save ----
    const ldapConfig = connector.ldapConfig

    const saveMutation = useEntityMutation({
        mutationFn: (request: LdapOutboundConfigUpdateRequest) =>
            services.outboundConnectors.updateLdapConfig(connector.id, request),
        invalidate: ['connector', connector.id],
        successMessage: i18nLdap.saved,
    })

    function handleSave() {
        // Validate before saving
        if (form.dnTemplate.trim() && !form.dnTemplate.includes('{ou}')) {
            updateForm({dnTemplateError: i18nLdap.dnTemplateHint})
            return
        }
        if (!validateDefaultOu(form.defaultOu, form.rules)) {
            return
        }

        if (!ldapConfig) return

        const serialized = serializeConfig(form)

        const request: LdapOutboundConfigUpdateRequest = {
            host: ldapConfig.host,
            port: ldapConfig.port,
            useSsl: ldapConfig.useSsl,
            useStarttls: ldapConfig.useStarttls,
            bindDn: ldapConfig.bindDn,
            bindPasswordRef: '',
            baseDn: ldapConfig.baseDn,
            userSearchBase: ldapConfig.userSearchBase ?? null,
            userSearchFilter: ldapConfig.userSearchFilter ?? null,
            groupSearchBase: ldapConfig.groupSearchBase ?? null,
            groupMemberAttribute: ldapConfig.groupMemberAttribute,
            userDnAttribute: ldapConfig.userDnAttribute,
            connectTimeoutMs: ldapConfig.connectTimeoutMs,
            responseTimeoutMs: ldapConfig.responseTimeoutMs,
            poolSize: ldapConfig.poolSize,
            version: ldapConfig.version,
            dnTemplate: serialized.dnTemplate,
            defaultOu: serialized.defaultOu,
            objectClasses: serialized.objectClasses,
            ouRoutingRules: serialized.ouRoutingRules,
        }
        saveMutation.mutate(request)
    }

    function handleCancel() {
        setForm(savedForm)
    }

    // ---- Read-only variant ----
    if (!canUpdate) {
        return (
            <Card data-testid="ldap-provisioning-config-card">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" data-testid="card-title-ldap-provisioning-config">
                        <Network className="h-5 w-5"/>
                        {i18nLdap.title}
                    </CardTitle>
                    <CardDescription>{i18nLdap.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <DetailField
                        label={i18nLdap.objectClasses}
                        value={savedConfig?.objectClasses && savedConfig.objectClasses.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                                {savedConfig.objectClasses.map((oc, i) => (

                                    <span key={i} className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium">
                                        {oc}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <MutedText>{i18nLdap.objectClassesEmpty}</MutedText>
                        )}
                    />

                    <DetailField
                        label={i18nLdap.dnTemplate}
                        value={savedConfig?.dnTemplate ? (

                            <code className="text-sm font-mono bg-muted rounded px-2 py-1 block">{savedConfig.dnTemplate}</code>
                        ) : null}
                    />

                    <div className="space-y-3">
                        <Label>{i18nLdap.ouRouting}</Label>
                        {savedConfig?.defaultOu && (
                            <DetailField
                                label={i18nLdap.defaultOu}
                                value={savedConfig.defaultOu}
                            />
                        )}
                        {savedConfig?.ouRoutingRules && savedConfig.ouRoutingRules.length > 0 && (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="ou-rules-readonly-table">
                                    <thead>
                                        <tr className="border-b text-muted-foreground">
                                            <th className="text-left py-2 pr-2 font-medium" data-testid="col-ou-attribute-readonly">{i18nLdap.attribute}</th>
                                            <th className="text-left py-2 pr-2 font-medium" data-testid="col-ou-operator-readonly">{i18nLdap.operator}</th>
                                            <th className="text-left py-2 pr-2 font-medium" data-testid="col-ou-value-readonly">{i18nLdap.value}</th>
                                            <th className="text-left py-2 pr-2 font-medium" data-testid="col-ou-target-readonly">{i18nLdap.targetOu}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {savedConfig.ouRoutingRules.map((rule, i) => (
                                            <tr key={i} className="border-b last:border-0">
                                                <td className="py-2 pr-2">{rule.attribute}</td>
                                                <td className="py-2 pr-2">{rule.operator}</td>
                                                <td className="py-2 pr-2">
                                                    {rule.operator === 'IN' && rule.value
                                                        ? rule.value.split('|').join(', ')
                                                        : rule.value}
                                                </td>
                                                <td className="py-2">{rule.targetOu}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {(!savedConfig?.ouRoutingRules || savedConfig.ouRoutingRules.length === 0) && !savedConfig?.defaultOu && (
                            <MutedText>—</MutedText>
                        )}
                    </div>
                </CardContent>
            </Card>
        )
    }

    // WHY: when the parent's GET has not yet resolved, hold back the editable
    // form so we never paint stale chips/inputs from a useState initializer
    // captured against an in-flight prop. CN0018 makes an empty-config state
    // unreachable on a saved connector, so we no longer surface the legacy
    // no-config banner here either (issue 736 A.1/A.2).
    if (!savedConfig) {
        return (
            <Card data-testid="ldap-provisioning-config-card">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" data-testid="card-title-ldap-provisioning-config">
                        <Network className="h-5 w-5"/>
                        {i18nLdap.title}
                    </CardTitle>
                    <CardDescription>{i18nLdap.description}</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center gap-2 py-6" data-testid="ldap-provisioning-config-loading">
                        <Loader2 className="h-4 w-4 animate-spin"/>
                        <MutedText size="sm">{i18n.common.loading}</MutedText>
                    </div>
                </CardContent>
            </Card>
        )
    }

    // ---- Edit variant ----
    return (
        <Card data-testid="ldap-provisioning-config-card">
            <CardHeader>
                <CardTitle className="flex items-center gap-2" data-testid="card-title-ldap-provisioning-config">
                    <Network className="h-5 w-5"/>
                    {i18nLdap.title}
                </CardTitle>
                <CardDescription>{i18nLdap.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Object Classes */}
                <div className="space-y-2">
                    <Label>{i18nLdap.objectClasses}</Label>
                    <TagInput
                        values={form.objectClasses}
                        onAdd={handleAddObjectClass}
                        onRemove={handleRemoveObjectClass}
                        pending={form.objectClassPending}
                        onPendingChange={v => updateForm({objectClassPending: v, objectClassError: null})}
                        maxItems={20}
                        placeholder={i18nLdap.objectClassesHint}
                        error={form.objectClassError}
                        hint={form.objectClasses.length === 0 ? i18nLdap.objectClassesEmpty : null}
                        testId="object-classes"
                    />
                </div>

                {/* DN Template */}
                <div className="space-y-2">
                    <Label>{i18nLdap.dnTemplate}</Label>
                    <Input
                        value={form.dnTemplate}
                        onChange={e => updateForm({dnTemplate: e.target.value, dnTemplateError: null})}
                        onBlur={handleDnTemplateBlur}
                        placeholder={i18nLdap.dnTemplateHint}
                        data-testid="ldap-dn-template"
                    />
                    {form.dnTemplateError && (
                        <MutedText size="xs" className="text-destructive" data-testid="dn-template-error">{form.dnTemplateError}</MutedText>
                    )}
                </div>

                {/* OU Routing Rules */}
                <div className="space-y-3">
                    <Label>{i18nLdap.ouRouting}</Label>
                    <OuRoutingRulesEditor
                        rules={form.rules}
                        defaultOu={form.defaultOu}
                        defaultOuError={form.defaultOuError}
                        onRulesChange={rules => updateForm({rules})}
                        onDefaultOuChange={v => updateForm({defaultOu: v, defaultOuError: null})}
                        onValidateDefaultOu={handleValidateDefaultOu}
                        canUpdate={canUpdate}
                        i18nLdap={i18nLdap}
                    />
                </div>

                {/* Save / Cancel */}
                {isDirty && (
                    <div className="flex justify-end gap-2 pt-2" data-testid="ldap-dirty-actions">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancel}
                            disabled={saveMutation.isPending}
                            data-testid="btn-ldap-cancel"
                        >
                            {i18n.common.cancel}
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSave}
                            disabled={saveMutation.isPending}
                            data-testid="btn-ldap-save"
                        >
                            {saveMutation.isPending ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin"/>
                                    {i18n.common.saving}
                                </>
                            ) : (
                                i18n.common.saveChanges
                            )}
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}
